import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface NeedLevel {
  level: number;
  name: string;
  nameEn: string;
  examples: string;
  color: string;
  category: 'deficiency' | 'growth';
}

interface PsychExperiment {
  name: string;
  researcher: string;
  finding: string;
  color: string;
}

interface ApplicationField {
  name: string;
  icon: string;
  desc: string;
}

interface MaslowNeedsTemplateProps {
  topic: string;
  subtitle: string;
  needs: NeedLevel[];
  experiments: PsychExperiment[];
  applications: ApplicationField[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: MaslowNeedsTemplateProps = {
  topic: '马斯洛需求层次理论',
  subtitle: "Maslow's Hierarchy of Needs",
  needs: [
    { level: 5, name: '自我实现', nameEn: 'Self-Actualization', examples: '创造力、使命感、高峰体验', color: '#f59e0b', category: 'growth' },
    { level: 4, name: '尊重需求', nameEn: 'Esteem', examples: '成就感、自信、他人尊重', color: '#22c55e', category: 'growth' },
    { level: 3, name: '社交需求', nameEn: 'Love & Belonging', examples: '友情、爱情、归属感', color: '#3b82f6', category: 'deficiency' },
    { level: 2, name: '安全需求', nameEn: 'Safety', examples: '人身安全、经济保障、健康', color: '#8b5cf6', category: 'deficiency' },
    { level: 1, name: '生理需求', nameEn: 'Physiological', examples: '食物、水、睡眠、呼吸', color: '#ef4444', category: 'deficiency' },
  ],
  experiments: [
    { name: '高峰体验研究', researcher: '马斯洛 (1964)', finding: '自我实现者更频繁地经历深刻的幸福和创造力高峰', color: '#f59e0b' },
    { name: '需求剥夺实验', researcher: '心理学经典', finding: '低层需求未满足时，高层需求的追求强度显著下降', color: '#3b82f6' },
    { name: '自我决定理论', researcher: 'Deci & Ryan', finding: '自主、胜任、关系三种心理需求是内在动机的基础', color: '#22c55e' },
  ],
  applications: [
    { name: '管理学', icon: '👔', desc: '根据员工需求层次设计激励方案' },
    { name: '教育学', icon: '📚', desc: '先满足安全感才能促进自主学习' },
    { name: '产品设计', icon: '📱', desc: '从功能需求到情感需求的体验升级' },
    { name: '心理咨询', icon: '🧠', desc: '评估来访者当前需求层次与困境' },
  ],
  stats: [
    { value: '1943', label: '理论首次发表年份' },
    { value: '5 层', label: '需求层次结构' },
    { value: '~2%', label: '马斯洛估计的自我实现比例' },
  ],
  bottomQuote: '一个人能够成为什么，他就必须成为什么——这就是自我实现 —— 马斯洛',
};

export const MaslowNeedsTemplate: React.FC<
  Partial<MaslowNeedsTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const pyramidStart = 40;
  const detailStart = 140;
  const experimentStart = 300;
  const appStart = 380;
  const statsStart = 450;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const pyramidLevels = [...p.needs].reverse();

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(155deg, #0c0618 0%, #120a24 50%, #0a0616 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: 40 }).map((_, i) => {
        const x = (i * 223) % 1920;
        const y = (i * 179) % 1080;
        const size = 1.5 + (i % 3);
        return (
          <div
            key={`dot-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: '50%',
              background: '#a78bfa',
              opacity: 0.06,
            }}
          />
        );
      })}

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 18}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#1e103a',
            borderRadius: 8,
            fontSize: 20,
            fontWeight: 700,
            color: '#c4b5fd',
            flexShrink: 0,
            border: '1px solid #c4b5fd30',
          }}
        >
          🧠 心理学
        </div>
        <h1 style={{ fontSize: 48, fontWeight: 900, margin: 0, color: '#f8fafc' }}>
          {p.topic}
        </h1>
        <p style={{ fontSize: 24, color: '#64748b', margin: 0, whiteSpace: 'nowrap' }}>
          {p.subtitle}
        </p>
      </div>

      {/* ===== CENTER: Pyramid SVG ===== */}
      <svg
        style={{ position: 'absolute', left: 100, top: 120 }}
        width="820"
        height="646"
        viewBox="0 0 660 520"
      >
        <text x="330" y="20" fill="#64748b" fontSize="20" textAnchor="middle" fontWeight="600">
          需求金字塔 · Hierarchy Pyramid
        </text>

        {pyramidLevels.map((need, i) => {
          const totalLevels = pyramidLevels.length;
          const topY = 40 + i * 90;
          const topWidth = 120 + i * 100;
          const bottomWidth = 120 + (i + 1) * 100;
          const centerX = 330;

          const layerOpacity = interpolate(
            frame,
            [pyramidStart + (totalLevels - 1 - i) * 20, pyramidStart + (totalLevels - 1 - i) * 20 + 25],
            [0, 1],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );

          const pathD = `M${centerX - topWidth / 2},${topY} L${centerX + topWidth / 2},${topY} L${centerX + bottomWidth / 2},${topY + 85} L${centerX - bottomWidth / 2},${topY + 85} Z`;

          return (
            <g key={i} opacity={layerOpacity}>
              <path
                d={pathD}
                fill={`${need.color}18`}
                stroke={need.color}
                strokeWidth="2"
              />
              <text
                x={centerX}
                y={topY + 30}
                fill={need.color}
                fontSize="22"
                textAnchor="middle"
                fontWeight="900"
              >
                {need.name}
              </text>
              <text
                x={centerX}
                y={topY + 53}
                fill="#94a3b8"
                fontSize="18"
                textAnchor="middle"
              >
                {need.nameEn}
              </text>
              <text
                x={centerX}
                y={topY + 77}
                fill="#64748b"
                fontSize="18"
                textAnchor="middle"
              >
                {need.examples}
              </text>
            </g>
          );
        })}

        {/* Category brackets */}
        {(() => {
          const bracketOpacity = interpolate(frame, [pyramidStart + 120, pyramidStart + 150], [0, 0.7], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <g opacity={bracketOpacity}>
              {/* Growth needs bracket */}
              <line x1="600" y1="45" x2="600" y2="215" stroke="#f59e0b" strokeWidth="2" />
              <line x1="595" y1="45" x2="610" y2="45" stroke="#f59e0b" strokeWidth="2" />
              <line x1="595" y1="215" x2="610" y2="215" stroke="#f59e0b" strokeWidth="2" />
              <text x="620" y="125" fill="#f59e0b" fontSize="19" fontWeight="700" dominantBaseline="middle">
                成长
              </text>
              <text x="620" y="145" fill="#f59e0b" fontSize="19" fontWeight="700" dominantBaseline="middle">
                需求
              </text>

              {/* Deficiency needs bracket */}
              <line x1="600" y1="225" x2="600" y2="485" stroke="#8b5cf6" strokeWidth="2" />
              <line x1="595" y1="225" x2="610" y2="225" stroke="#8b5cf6" strokeWidth="2" />
              <line x1="595" y1="485" x2="610" y2="485" stroke="#8b5cf6" strokeWidth="2" />
              <text x="620" y="345" fill="#8b5cf6" fontSize="19" fontWeight="700" dominantBaseline="middle">
                缺失
              </text>
              <text x="620" y="365" fill="#8b5cf6" fontSize="19" fontWeight="700" dominantBaseline="middle">
                需求
              </text>
            </g>
          );
        })()}
      </svg>

      {/* ===== RIGHT: 层次解析 + 相关研究（横向两列，研究纵向） ===== */}
      <div
        style={{
          position: 'absolute',
          top: 130,
          left: 950,
          right: 44,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 28,
        }}
      >
        <div style={{ flex: '0 0 420px', minWidth: 0 }}>
          <div
            style={{
              fontSize: 26,
              color: '#64748b',
              marginBottom: 12,
              letterSpacing: '0.08em',
              fontWeight: 600,
            }}
          >
            层次解析
          </div>
          {p.needs.map((need, i) => {
            const s = spring({
              frame: frame - (detailStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 10,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: `${need.color}20`,
                    border: `2px solid ${need.color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    fontWeight: 900,
                    color: need.color,
                    flexShrink: 0,
                  }}
                >
                  {need.level}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: need.color }}>
                    {need.name}
                    <span style={{ fontSize: 20, color: '#64748b', fontWeight: 500, marginLeft: 8 }}>
                      {need.category === 'growth' ? '成长型' : '缺失型'}
                    </span>
                  </div>
                  <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>{need.examples}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              fontSize: 26,
              color: '#64748b',
              marginBottom: 0,
              letterSpacing: '0.08em',
              fontWeight: 600,
            }}
          >
            相关研究
          </div>
          {p.experiments.map((exp, i) => {
            const s = spring({
              frame: frame - (experimentStart + i * 20),
              fps,
              config: { damping: 14, stiffness: 110 },
            });
            return (
              <div
                key={i}
                style={{
                  width: '100%',
                  background: '#0f0a1e',
                  borderRadius: 14,
                  padding: '14px 16px',
                  borderTop: `3px solid ${exp.color}`,
                  opacity: s,
                  transform: `translateY(${(1 - s) * 24}px)`,
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 800, color: exp.color, marginBottom: 4 }}>
                  {exp.name}
                </div>
                <div style={{ fontSize: 22, color: '#64748b', marginBottom: 6 }}>
                  📖 {exp.researcher}
                </div>
                <div style={{ fontSize: 20, color: '#cbd5e1', lineHeight: 1.45 }}>
                  {exp.finding}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Applications ===== */}
      <div style={{ position: 'absolute', top: 750, left: 40, right: 40 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          应用领域
        </div>
        <div style={{ display: 'flex', gap: 14 }}>
          {p.applications.map((app, i) => {
            const s = spring({
              frame: frame - (appStart + i * 14),
              fps,
              config: { damping: 14, stiffness: 130 },
            });
            const colors = ['#f59e0b', '#3b82f6', '#22c55e', '#8b5cf6'];
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  background: '#0f0a1e',
                  borderRadius: 12,
                  padding: '14px 14px',
                  border: `1px solid ${colors[i]}30`,
                  opacity: s,
                  transform: `scale(${0.8 + 0.2 * s})`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <div style={{ fontSize: 30, lineHeight: 1, flexShrink: 0 }}>{app.icon}</div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ fontSize: 22, fontWeight: 800, color: colors[i] }}>{app.name}</div>
                    <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>{app.desc}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Stats ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          left: 40,
          right: 40,
          display: 'flex',
          gap: 16,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0f0a1e',
                borderRadius: 12,
                padding: '14px 10px',
                border: '1px solid #1e1b4b',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 30, fontWeight: 900, color: '#c4b5fd', flexShrink: 0 }}>{st.value}</span>
              <span style={{ fontSize: 22, color: '#64748b' }}>{st.label}</span>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 15,
          left: 40,
          right: 40,
          background: '#0f0a1e',
          borderRadius: 14,
          padding: '14px 20px',
          borderTop: '3px solid #c4b5fd',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🧠</span>
          <span style={{ fontSize: 20, color: '#e2e8f0', lineHeight: 1.5 }}>{p.bottomQuote}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #ef4444, #8b5cf6, #3b82f6, #22c55e, #f59e0b)',
        }}
      />
    </AbsoluteFill>
  );
};
